import './alteration-line.scss'

import _ from 'lodash'
import { first } from 'rxjs/operators'
import { PropType } from 'vue'
import Component from "vue-class-component"
import { BModal } from 'bootstrap-vue'
import { faTimesCircle, faComment, faImage } from '@fortawesome/free-regular-svg-icons'

import { Alteration, createTranslatedString, editTranslatedString, getTranslatedString } from '@skinsoft/types'
import { preferredTerm } from '@skinsoft/selectors/thesaurus-term-selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity'

import { ReactiveVue } from "@skinsoft/reactive-vue"
import { EntitySelect } from '@skinsoft/ui'
import ThesaurusTermTree from '@skinsoft/ui/src/thesaurus-term-tree'
import { THESAURUS_TERM_SELECTED } from '@skinsoft/ui/src/thesaurus-term-tree/thesaurus-term-tree-modal'
import { AutoCompleteTextField2, FIELD_VALUE_UPDATED } from '@skinsoft/ui2'
import { AlterationDetailImagePopup } from '../alteration-detail-image-popup'
import { DETAILED_ALTERATION_IMAGE_ADDED } from '../../../../events'
import { DELETE_IMAGE_CLICK_TOPIC } from '@skinsoft/bus'
import { DeleteImageClickEvent } from '@skinsoft/entity-ui'

export const DELETE_ALTERATION = 'delete-alteration'
export const ALTERATION_EDITED = 'alteration-edited'

export interface AlterationChanged {
    alterationNumber: number,
    updates: Partial<Alteration>
}

@Component({
    components: {
        sEntitySelect: EntitySelect,
        sThesaurusTermTree: ThesaurusTermTree,
        sAutoCompleteTextField2: AutoCompleteTextField2,
        sAlterationDetailImagePopup: AlterationDetailImagePopup,
    },
    props: {
        alteration: {
            type: Object as PropType<Alteration>,
        },
        alterationNumber: { type: Number },
        readOnly: { type: Boolean },
        existingTitles: {
            type: Array as PropType<String[]>,
            default: () => []
        },
    },
    template: require('./alteration-line.html')
})
export default class AlterationLine extends ReactiveVue {
    
    private alteration: Alteration
    private alterationNumber: Number
    private readOnly: Boolean
    private existingTitles: String[]

    public $thesaurusTermDataStore: ThesaurusTermDataStore

    private selectedAlterationType: string = '' // id of ThesaurusTerm
    private alterationCopy: Alteration | null = null

    public $refs: {
        precisionModal: BModal,
        detailedImagesModal: AlterationDetailImagePopup,
    }

    public deleteIcon = faTimesCircle
    public commentIcon = faComment
    public imageIcon = faImage

    private DELETE_ALTERATION = DELETE_ALTERATION
    private THESAURUS_TERM_SELECTED = THESAURUS_TERM_SELECTED
    private FIELD_VALUE_UPDATED = FIELD_VALUE_UPDATED
    private DETAILED_ALTERATION_IMAGE_ADDED = DETAILED_ALTERATION_IMAGE_ADDED
    private DELETE_IMAGE_CLICK_TOPIC = DELETE_IMAGE_CLICK_TOPIC

    private mounted() {
        if(this.alteration) {
            this.alterationCopy = _.cloneDeep(this.alteration)
            if(this.alteration.typeOfAlteration) {
                this.selectedAlterationType = this.alteration.typeOfAlteration
            }
        }
    }

    private async getThesaurusTermNameIfExists(id: string) {
        if (id) {
            const thesaurusTerm = await this.$thesaurusTermDataStore.fetchOne(id).pipe(first()).toPromise();
            if (thesaurusTerm) {
                return getTranslatedString(
                    preferredTerm(thesaurusTerm),
                    this.$i18n.locale
                )
            }
        }
        return ''
    }

    private onThesaurusTermSelected(thesaurusTermId: string) {
        if(thesaurusTermId) {
            const updates: Partial<Alteration> = {
                typeOfAlteration: thesaurusTermId
            }

            this.selectedAlterationType = thesaurusTermId
            this.$emit(
                ALTERATION_EDITED, 
                { 
                    alterationNumber: this.alterationNumber, 
                    updates 
                } as AlterationChanged
            )
        }
    }

    private openPrecisionModal() {
        // @ts-ignore
        this.$refs.precisionModal.show()
    }

    private onAlterationPrecisionEdited() {
        if(this.alterationCopy) {
            const updates: Partial<Alteration> = {
                precision: this.alterationCopy.precision,
            }

            this.$emit(
                ALTERATION_EDITED, 
                { 
                    alterationNumber: this.alterationNumber, 
                    updates 
                } as AlterationChanged
            )
        }
        // @ts-ignore
        this.$refs.precisionModal.hide()
    }

    private deleteAlteration() {
        if(!this.readOnly) {
            this.$emit(DELETE_ALTERATION, this.alterationNumber)
        }
    }

    private openDetailedImageModal() {
        this.$refs.detailedImagesModal.openModal()
    }

    private get precision(): string {
        if(this.alterationCopy) {
            return getTranslatedString(this.alterationCopy.precision, this.$i18n.locale)
        }

        return ''
    }
    private set precision(value: string) {
        if(this.alterationCopy) {
            if(this.alterationCopy.precision) {
                editTranslatedString(this.alterationCopy.precision, value, this.$i18n.locale)
            } else {
                _.set(this.alterationCopy, 'precision', createTranslatedString(this.$i18n, value))
            }
        }
    }

    private get title(): string {
        if(this.alterationCopy) {
            return getTranslatedString(this.alterationCopy.title, this.$i18n.locale)
        }

        return ''
    }
    private set title(value: string) {
        if(this.alterationCopy) {
            if(this.alterationCopy.title) {
                editTranslatedString(this.alterationCopy.title, value, this.$i18n.locale)
            } else {
                _.set(this.alterationCopy, 'title', createTranslatedString(this.$i18n.locale, value))
            }

            const updates: Partial<Alteration> = {
                title: this.alterationCopy.title,
            }
            this.$emit(
                ALTERATION_EDITED, 
                { 
                    alterationNumber: this.alterationNumber, 
                    updates 
                } as AlterationChanged
            )
        }
    }

    private get detailedImages(): string[] {
        return this.alterationCopy?.detailedViews ?? []
    }

    private onDetailedImageAdded(event) {
        if(event.cacheKey) {
            if(this.alterationCopy) {
                if(this.alterationCopy.detailedViews) {
                    this.alterationCopy.detailedViews.push(event.cacheKey)
                } else {
                    this.alterationCopy.detailedViews = [event.cacheKey]
                }

                const updates: Partial<Alteration> = {
                    detailedViews: this.alterationCopy.detailedViews,
                }
                this.$emit(
                    ALTERATION_EDITED, 
                    { 
                        alterationNumber: this.alterationNumber, 
                        updates 
                    } as AlterationChanged
                )
            }
        }
    }

    private onDetailedImageDeleted(event: DeleteImageClickEvent) {
        if(this.alterationCopy?.detailedViews?.length) {
            if(event.imageId) {
                this.alterationCopy.detailedViews = this.alterationCopy.detailedViews.filter(mediaId => {
                    return mediaId !== event.imageId
                })

                const updates: Partial<Alteration> = {
                    detailedViews: this.alterationCopy.detailedViews,
                }
                this.$emit(
                    ALTERATION_EDITED, 
                    { 
                        alterationNumber: this.alterationNumber, 
                        updates 
                    } as AlterationChanged
                )
            }
        }
    }

    private onTitleUpdated(value: String) {
        this.title = value as string
    }
}